import {
  Calendar,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  RefreshCw,
  User
} from 'lucide-react';
import { useContext, useEffect, useState } from 'react';

import Alert from '@/components/Alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ChainlitContext } from 'client-types/*';

interface AdminUser {
  id: string;
  identifier: string;
  role: string;
  provider: string;
  created_at: string;
  subscription_status?: string;
}

interface UserSubscription {
  id: string;
  plan_name: string;
  status: string;
  billing_cycle: string;
  amount_cents: number;
  currency: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
}

interface UserBilling {
  invoice_id: string;
  amount_due: number;
  amount_paid?: number;
  currency: string;
  status: string;
  created_at: string;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
}

interface UserDetailModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
}

const UserDetailModal = ({ user, isOpen, onClose }: UserDetailModalProps) => {
  const [subscription, setSubscription] = useState<UserSubscription | null>(
    null
  );
  const [billingHistory, setBillingHistory] = useState<UserBilling[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const apiClient = useContext(ChainlitContext);

  const fetchUserDetails = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError('');

      // Fetch user subscription
      const subResponse = await fetch(
        apiClient.buildEndpoint(`/api/admin/users/${user.id}/subscription`),
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSubscription(subData.subscription);
      } else {
        setSubscription(null);
      }

      // Fetch user billing history
      const billingResponse = await fetch(
        apiClient.buildEndpoint(`/api/admin/users/${user.id}/billing`),
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (billingResponse.ok) {
        const billingData = await billingResponse.json();
        setBillingHistory(billingData.billing_history || []);
      } else {
        setBillingHistory([]);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchUserDetails();
    }
  }, [isOpen, user]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const getRoleBadgeColor = (role: string) => {
    return role === 'ADMIN' ? 'default' : 'secondary';
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'canceled':
        return 'destructive';
      case 'past_due':
        return 'destructive';
      case 'paid':
        return 'default';
      case 'open':
        return 'secondary';
      case 'void':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
            <User className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="truncate">User Details: {user.identifier}</span>
          </DialogTitle>
        </DialogHeader>

        {error && <Alert variant="error">{error}</Alert>}

        <div className="space-y-6">
          {/* User Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 border rounded-lg">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Email
              </label>
              <p className="font-medium text-sm sm:text-base break-all">
                {user.identifier}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Role
              </label>
              <div className="mt-1">
                <Badge variant={getRoleBadgeColor(user.role)}>
                  {user.role}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Provider
              </label>
              <p className="text-sm sm:text-base">{user.provider}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Joined
              </label>
              <p className="text-sm sm:text-base">
                {formatDate(user.created_at)}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-6">Loading user details...</div>
          ) : (
            <Tabs defaultValue="subscription" className="w-full">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger
                  value="subscription"
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                >
                  <CreditCard className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Subscription</span>
                  <span className="sm:hidden">Sub</span>
                </TabsTrigger>
                <TabsTrigger
                  value="billing"
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                >
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Billing History</span>
                  <span className="sm:hidden">Billing</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="subscription" className="mt-6">
                {subscription ? (
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Plan
                        </label>
                        <p className="font-medium text-sm sm:text-base">
                          {subscription.plan_name}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Status
                        </label>
                        <div className="mt-1">
                          <Badge
                            variant={getStatusBadgeColor(subscription.status)}
                          >
                            {subscription.status}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Amount
                        </label>
                        <p className="font-mono text-sm sm:text-base">
                          {formatAmount(
                            subscription.amount_cents,
                            subscription.currency
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Billing
                        </label>
                        <p className="text-sm sm:text-base">
                          {subscription.billing_cycle}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Current Period
                        </label>
                        <p className="text-sm sm:text-base">
                          {formatDate(subscription.current_period_start)} -{' '}
                          {formatDate(subscription.current_period_end)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Created
                        </label>
                        <p className="text-sm sm:text-base">
                          {formatDate(subscription.created_at)}
                        </p>
                      </div>
                    </div>

                    {subscription.cancel_at_period_end && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-sm text-yellow-800">
                          This subscription will cancel at the end of the
                          current period.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          window.open(
                            `https://dashboard.stripe.com/subscriptions/${subscription.id}`,
                            '_blank'
                          )
                        }
                        className="text-xs sm:text-sm"
                      >
                        <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">View in Stripe</span>
                        <span className="sm:hidden">Stripe</span>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No active subscription found for this user.
                  </div>
                )}
              </TabsContent>

              <TabsContent value="billing" className="mt-6">
                {billingHistory.length > 0 ? (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden sm:block border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {billingHistory.map((bill) => (
                            <TableRow key={bill.invoice_id}>
                              <TableCell className="font-mono text-sm">
                                {bill.invoice_id.slice(0, 12)}...
                              </TableCell>
                              <TableCell className="font-mono">
                                <div>
                                  <div>
                                    Due:{' '}
                                    {formatAmount(
                                      bill.amount_due,
                                      bill.currency
                                    )}
                                  </div>
                                  {bill.amount_paid !== null &&
                                    bill.amount_paid !== undefined && (
                                      <div className="text-sm text-green-600">
                                        Paid:{' '}
                                        {formatAmount(
                                          bill.amount_paid,
                                          bill.currency
                                        )}
                                      </div>
                                    )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={getStatusBadgeColor(bill.status)}
                                >
                                  {bill.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {formatDate(bill.created_at)}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {bill.invoice_pdf && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        window.open(bill.invoice_pdf, '_blank')
                                      }
                                      className="h-8 w-8 p-0"
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {bill.hosted_invoice_url && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        window.open(
                                          bill.hosted_invoice_url,
                                          '_blank'
                                        )
                                      }
                                      className="h-8 w-8 p-0"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      window.open(
                                        `https://dashboard.stripe.com/invoices/${bill.invoice_id}`,
                                        '_blank'
                                      )
                                    }
                                    className="h-8 w-8 p-0"
                                  >
                                    <Calendar className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="sm:hidden space-y-3">
                      {billingHistory.map((bill) => (
                        <div
                          key={bill.invoice_id}
                          className="border rounded-lg p-4 space-y-3"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-xs text-muted-foreground">
                                Invoice
                              </div>
                              <div className="font-mono text-sm">
                                {bill.invoice_id.slice(0, 12)}...
                              </div>
                            </div>
                            <Badge variant={getStatusBadgeColor(bill.status)}>
                              {bill.status}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <div className="text-xs text-muted-foreground">
                                Amount Due
                              </div>
                              <div className="font-mono text-sm">
                                {formatAmount(bill.amount_due, bill.currency)}
                              </div>
                              {bill.amount_paid !== null &&
                                bill.amount_paid !== undefined && (
                                  <div className="text-xs text-green-600">
                                    Paid:{' '}
                                    {formatAmount(
                                      bill.amount_paid,
                                      bill.currency
                                    )}
                                  </div>
                                )}
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">
                                Date
                              </div>
                              <div className="text-sm">
                                {formatDate(bill.created_at)}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2 pt-2">
                            {bill.invoice_pdf && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  window.open(bill.invoice_pdf, '_blank')
                                }
                                className="flex-1 text-xs"
                              >
                                <Download className="h-3 w-3 mr-1" />
                                PDF
                              </Button>
                            )}
                            {bill.hosted_invoice_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  window.open(bill.hosted_invoice_url, '_blank')
                                }
                                className="flex-1 text-xs"
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                View
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                window.open(
                                  `https://dashboard.stripe.com/invoices/${bill.invoice_id}`,
                                  '_blank'
                                )
                              }
                              className="flex-1 text-xs"
                            >
                              <Calendar className="h-3 w-3 mr-1" />
                              Stripe
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No billing history found for this user.
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button
            onClick={fetchUserDetails}
            variant="outline"
            size="sm"
            className="text-xs sm:text-sm"
          >
            <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">↻</span>
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            className="text-xs sm:text-sm"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailModal;
